'use client';

import Link from 'next/link';
import { useCallback, useState } from 'react';
import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpRight, ChevronRight } from 'lucide-react';
import { Box, Button, ButtonBase, Checkbox, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/navigation';

import { resolveScreenRoute, type SiteScreen } from '@/ai/screens/site-screen-catalog';
import type { RendererDescriptor } from '@/ai/tools/types';
import { SCREENER_COLUMNS, type ScreenerColumnFormat } from '@/ai/screener/screener-catalog';
import { ComparadorClient } from '@/app/(public)/comparador/_components/ComparadorClient';
import { ExternalLinkConfirmDialog, useExternalLinkConfirm } from './external-link-confirm';

export type { RendererDescriptor } from '@/ai/tools/types';

export type RendererKind =
  | 'screen-list'
  | 'screen-link'
  | 'external-link'
  | 'ticker-summary'
  | 'screener-results'
  | 'comparator'
  | 'related-funds'
  | 'fund-dividends'
  | 'market-overview'
  | 'market-series'
  | 'web-search'
  | 'fund-reports'
  | 'report-details'
  | 'saved-searches'
  | 'portfolio-list'
  | 'portfolio-overview'
  | 'portfolio-dividends'
  | 'portfolio-patrimony'
  | 'portfolio-diversification'
  | 'portfolio-processing-status'
  | 'fixed-income-details'
  | 'fixed-income-search-results'
  | 'fixed-income-comparison'
  | 'fixed-income-history'
  | 'portfolio-positions'
  | 'portfolio-movements'
  | 'course-lesson'
  | 'course-search-results'
  | 'course-outline';

export function summarizeRenderer(descriptor: RendererDescriptor): string {
  const params = descriptor.params ?? {};
  const data = (descriptor.data ?? {}) as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

  switch (descriptor.kind) {
    case 'ticker-summary':
      return `Ferramenta · Resumo de FII · ${text(params.ticker) ?? '—'}`;
    case 'screener-results': {
      const filters = Array.isArray(data.filterSummary) ? (data.filterSummary as string[]).join(' · ') : 'todos';
      const count = typeof data.count === 'number' ? data.count : 0;
      return `Ferramenta · Busca de fundos · ${filters} — ${count} fundos`;
    }
    case 'comparator': {
      const tickers = Array.isArray(params.tickers) ? (params.tickers as string[]).join(' × ') : '—';
      return `Ferramenta · Comparador · ${tickers}`;
    }
    case 'related-funds':
      return `Ferramenta · Fundos similares · ${text(params.ticker) ?? '—'}`;
    case 'fund-dividends':
      return `Ferramenta · Proventos · ${text(params.ticker) ?? '—'}`;
    case 'screen-link':
      return `Ferramenta · Tela · ${text(data.title) ?? text(params.screenId) ?? '—'}`;
    case 'screen-list':
      return 'Ferramenta · Telas disponíveis';
    case 'external-link':
      return `Ferramenta · Site externo · ${text(params.url) ?? '—'}`;
    case 'market-overview':
      return 'Ferramenta · Visão geral do mercado';
    case 'market-series': {
      const indicators = Array.isArray(params.indicators) ? (params.indicators as string[]).join(', ') : '—';
      return `Ferramenta · Série de mercado · ${indicators} · ${text(params.fromMonth) ?? '—'}–${text(params.toMonth) ?? '—'}`;
    }
    case 'web-search':
      return `Ferramenta · Busca na web · ${text(params.query) ?? '—'}`;
    case 'fund-reports':
      return `Ferramenta · Relatórios · ${text(params.ticker) ?? '—'}`;
    case 'report-details':
      return `Ferramenta · Conteúdo do relatório · ${text(params.ticker) ?? '—'}#${text(params.documentId) ?? '—'}`;
    case 'fixed-income-details':
      return `Ferramenta · Detalhes do título · ${text(params.idCem) ?? '—'}`;
    case 'fixed-income-search-results':
      return `Ferramenta · Busca de renda fixa · ${typeof data.count === 'number' ? data.count : 0} títulos`;
    case 'fixed-income-comparison':
      return `Ferramenta · Comparação de títulos · ${Array.isArray(params.idCems) ? params.idCems.join(' × ') : '—'}`;
    case 'fixed-income-history':
      return `Ferramenta · Histórico do título · ${text(params.idCem) ?? '—'}`;
    case 'portfolio-positions':
      return `Ferramenta · Posições da carteira · ${typeof data.count === 'number' ? data.count : 0} ativos`;
    case 'portfolio-movements':
      return `Ferramenta · Movimentações da carteira · ${typeof data.count === 'number' ? data.count : 0} registros`;
    case 'saved-searches':
      return 'Ferramenta · Buscas salvas';
    case 'portfolio-list':
      return 'Ferramenta · Carteiras do usuário';
    case 'portfolio-overview':
      return `Ferramenta · Resumo da carteira · ${text(params.portfolioId) ?? 'ativa'}`;
    case 'portfolio-dividends':
      return 'Ferramenta · Dividendos da carteira';
    case 'portfolio-patrimony':
      return 'Ferramenta · Patrimônio da carteira';
    case 'portfolio-diversification':
      return 'Ferramenta · Diversificação da carteira';
    case 'portfolio-processing-status':
      return 'Ferramenta · Processamento da carteira';
    case 'course-lesson': {
      const resume =
        typeof data.resume === 'object' && data.resume !== null ? (data.resume as Record<string, unknown>) : {};
      return `Ferramenta · Progresso no curso · ${text(resume.topicTitle) ?? 'sem progresso ainda'}`;
    }
    case 'course-search-results':
      return `Ferramenta · Busca nos cursos · ${text(params.query) ?? '—'} — ${
        typeof data.count === 'number' ? data.count : 0
      } aulas`;
    case 'course-outline':
      return `Ferramenta · Sumário dos cursos · ${text(params.course) ?? text(params.track) ?? 'trilhas e cursos'}`;
    default:
      return 'Ferramenta';
  }
}

const TOOL_LABELS: Record<string, string> = {
  list_screens: 'telas disponíveis',
  link_to_screen: 'tela do site',
  link_external: 'site externo',
  ticker_summary: 'resumo do fundo',
  screener_search: 'busca de fundos',
  comparator_fundamentals: 'comparador de fundos',
  related_funds: 'fundos similares',
  fund_dividends_history: 'proventos do fundo',
  market_overview: 'panorama do mercado',
  market_series: 'série de mercado',
  web_search: 'busca na web',
  fund_reports: 'relatórios do fundo',
  report_details: 'conteúdo do relatório',
  saved_searches: 'buscas salvas',
  list_user_portfolios: 'carteiras do usuário',
  portfolio_overview: 'resumo da carteira',
  portfolio_dividends: 'dividendos da carteira',
  portfolio_patrimony: 'patrimônio da carteira',
  portfolio_diversification: 'diversificação da carteira',
  portfolio_processing_status: 'processamento da carteira',
  fixed_income_details: 'detalhes do título',
  fixed_income_search: 'busca de renda fixa',
  fixed_income_compare: 'comparação de títulos',
  fixed_income_history: 'histórico do título',
  portfolio_positions: 'posições da carteira',
  portfolio_movements: 'movimentações da carteira',
  search_course_content: 'busca nos cursos',
  get_my_course_status: 'progresso no curso',
  get_course_outline: 'sumário dos cursos',
  get_course_lesson: 'conteúdo da aula',
};

/** Friendly pt-BR name shown in the "consultando…" streaming indicator. */
export const toolDisplayName = (name: string) => TOOL_LABELS[name] ?? name;

/** Consistent source/as-of footnote shared by the data cards. */
export function RendererFooter({
  asOf,
  period,
  screenId,
  params,
  linkLabel = 'Abrir tela',
}: {
  asOf?: unknown;
  period?: string;
  screenId?: string;
  params?: Record<string, string>;
  linkLabel?: string;
}) {
  const formattedDate =
    typeof asOf === 'string' && asOf
      ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${asOf.slice(0, 10)}T00:00:00Z`))
      : null;
  if (!formattedDate && !period) return null;
  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      spacing={1}
      sx={{ mt: 1, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant='caption' color='text.secondary'>
        {period ? `Período: ${period}` : `Dados até ${formattedDate}`}
      </Typography>
      {screenId ? <OpenExternalBtn screenId={screenId} params={params} label={linkLabel} /> : null}
    </Stack>
  );
}

export function useNavigateTo() {
  const router = useRouter();
  return useCallback(
    (screenId: string, params?: Record<string, string>) => {
      const route = resolveScreenRoute(screenId, params);
      if (route) router.push(route);
    },
    [router],
  );
}

export function OpenExternalBtn({
  screenId,
  params,
  label = 'Abrir em nova aba',
}: {
  screenId: string;
  params?: Record<string, string>;
  label?: string;
}) {
  const route = resolveScreenRoute(screenId, params);
  if (!route) return null;
  return (
    <IconButton
      component='a'
      href={route}
      target='_blank'
      rel='noopener noreferrer'
      size='small'
      aria-label={label}
      title={label}
    >
      <ArrowUpRight size={16} aria-hidden='true' />
    </IconButton>
  );
}

function ExternalLinkRenderer({ params }: { params: Record<string, unknown> }) {
  const url = typeof params.url === 'string' ? params.url : null;
  const confirmation = useExternalLinkConfirm();
  if (!url) return null;

  return (
    <>
      <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
        <Typography variant='body2' fontWeight={700}>
          Site externo
        </Typography>
        <Typography variant='caption' color='text.secondary' display='block'>
          Este link abre um site fora do Análise de FIIs Pro.
        </Typography>
        <Typography
          variant='caption'
          display='block'
          sx={{ overflowWrap: 'anywhere', fontFamily: 'monospace', my: 0.75 }}
        >
          {url}
        </Typography>
        <ButtonBase
          onClick={() => confirmation.request(url)}
          sx={{ px: 1, py: 0.5, borderRadius: 1, typography: 'button' }}
        >
          Abrir
        </ButtonBase>
      </Paper>
      <ExternalLinkConfirmDialog
        url={url}
        open={confirmation.pendingUrl !== null}
        onClose={confirmation.close}
        onConfirm={confirmation.confirm}
      />
    </>
  );
}

function ScreenListRenderer({ data }: { data: unknown }) {
  const navigateTo = useNavigateTo();
  if (!Array.isArray(data)) return null;
  const screens = data.filter(
    (screen): screen is SiteScreen =>
      typeof screen === 'object' &&
      screen !== null &&
      typeof (screen as SiteScreen).id === 'string' &&
      typeof (screen as SiteScreen).title === 'string' &&
      typeof (screen as SiteScreen).description === 'string',
  );

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {screens.map((screen) => (
        <ButtonBase
          key={screen.id}
          onClick={() => navigateTo(screen.id)}
          sx={{
            display: 'block',
            width: '100%',
            p: 1,
            borderRadius: 1,
            textAlign: 'left',
            '&:hover': { bgcolor: 'hsl(var(--muted))' },
          }}
        >
          <Typography variant='body2' fontWeight={700}>
            {screen.title}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {screen.description}
          </Typography>
        </ButtonBase>
      ))}
    </Stack>
  );
}

function ScreenLinkRenderer({ params, data }: { params: Record<string, unknown>; data: unknown }) {
  const navigateTo = useNavigateTo();
  const screenId = typeof params.screenId === 'string' ? params.screenId : null;
  const navigationParams =
    typeof params.params === 'object' && params.params !== null
      ? Object.fromEntries(Object.entries(params.params).filter(([, value]) => typeof value === 'string'))
      : undefined;
  const linkData =
    typeof data === 'object' && data !== null ? (data as { title?: unknown; description?: unknown }) : {};
  const title = typeof linkData.title === 'string' ? linkData.title : null;
  const description = typeof linkData.description === 'string' ? linkData.description : null;

  if (!screenId || !title) return null;

  return (
    <Paper variant='outlined' sx={{ mt: 1, overflow: 'hidden' }}>
      <ButtonBase
        component='div'
        onClick={() => navigateTo(screenId, navigationParams)}
        sx={{
          display: 'block',
          width: '100%',
          p: 1.25,
          textAlign: 'left',
          '&:hover': { bgcolor: 'hsl(var(--muted))' },
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='body2' fontWeight={700}>
              {title}
            </Typography>
            {description ? (
              <Typography variant='caption' color='text.secondary'>
                {description}
              </Typography>
            ) : null}
          </Box>
          <Box onClick={(event) => event.stopPropagation()}>
            <OpenExternalBtn screenId={screenId} params={navigationParams} />
          </Box>
        </Stack>
      </ButtonBase>
    </Paper>
  );
}

function InstrumentSummaryRenderer({
  params,
  data,
  onAsk,
}: {
  params: Record<string, unknown>;
  data: unknown;
  onAsk?: (prompt: string) => void;
}) {
  const navigateTo = useNavigateTo();
  const ticker = typeof params.ticker === 'string' ? params.ticker : null;
  const values = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  const name = typeof values.name === 'string' ? values.name : ticker;
  const formatCurrency = (value: unknown) =>
    typeof value === 'number'
      ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        }).format(value)
      : '—';
  const formatCompactCurrency = (value: unknown) => {
    if (typeof value !== 'number') return '—';
    const abs = Math.abs(value);
    const formatter = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (abs >= 1_000_000_000) return `R$ ${formatter.format(value / 1_000_000_000)} bi`;
    if (abs >= 1_000_000) return `R$ ${formatter.format(value / 1_000_000)} mi`;
    if (abs >= 1_000) return `R$ ${formatter.format(value / 1_000)} mil`;
    return formatCurrency(value);
  };
  const formatNumber = (value: unknown, digits = 2) =>
    typeof value === 'number'
      ? new Intl.NumberFormat('pt-BR', {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(value)
      : '—';
  const formatInteger = (value: unknown) =>
    typeof value === 'number' ? new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value) : '—';
  const formatPercent = (value: unknown) => (typeof value === 'number' ? `${formatNumber(value)}%` : '—');
  const lastPriceDate = typeof values.lastPriceDate === 'string' ? values.lastPriceDate : null;
  const formattedDate = lastPriceDate
    ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${lastPriceDate}T00:00:00Z`))
    : '—';

  if (!ticker) return null;

  const metric = (label: string, value: string) => <Metric key={label} label={label} value={value} />;
  const sectionLabel = (label: string) => (
    <Typography
      variant='caption'
      color='text.secondary'
      fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
  );

  return (
    <Paper
      variant='outlined'
      sx={{
        mt: 1,
        p: 1.5,
        borderRadius: 1.5,
        borderColor: 'hsl(var(--border))',
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant='body2' fontWeight={700} noWrap>
              {name || 'Fundo imobiliário'}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'monospace' }}>
              {ticker}
            </Typography>
          </Box>
          <OpenExternalBtn screenId='fii-resumo' params={{ ticker }} />
        </Stack>
        <Stack direction='row' spacing={0.75} sx={{ minWidth: 0, overflow: 'hidden' }}>
          {[values.classificacao, values.segmento, values.gestao].map((value, index) => (
            <Typography
              key={index}
              variant='caption'
              color='text.secondary'
              noWrap
              sx={{
                maxWidth: index === 2 ? '45%' : undefined,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {typeof value === 'string' && value ? value : '—'}
              {index < 2 ? ' ·' : ''}
            </Typography>
          ))}
        </Stack>
        <Box>
          <Typography variant='h6' fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(values.currentPrice)}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            última cotação · {formattedDate}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            metric('Último dividendo', formatCurrency(values.latestDividend)),
            metric('VP/cota', formatCurrency(values.vpPerShare)),
            metric('Patrimônio', formatCompactCurrency(values.patrimonio)),
            metric('P/VP', formatNumber(values.pvp)),
            metric('Valor de mercado', formatCompactCurrency(values.marketValue)),
            metric('DY 12m', formatPercent(values.dy12mPct)),
            metric('Dividendos 12m', formatCurrency(values.dividends12m)),
            metric('Cotistas', formatInteger(values.numCotistas)),
            metric('Cotas emitidas', formatInteger(values.numCotas)),
          ]}
        </Box>
        {sectionLabel('Indicadores')}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            metric(
              'Sensibilidade à Selic',
              `${formatPercent(values.selicSensitivityPct)} · ${values.selicSensitivityLabel ?? '—'}`,
            ),
            metric('Índice de alavancagem', `${formatPercent(values.leveragePct)} · ${values.leverageLabel ?? '—'}`),
            metric('Distância da máxima 12m', formatPercent(values.distanceToMaxPct)),
            metric('Vacância média', formatPercent(values.vacanciaPct)),
            metric('Inadimplência média', formatPercent(values.inadimplenciaPct)),
          ]}
        </Box>
        {sectionLabel('Risco e momento')}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            metric('Volatilidade 21d', formatPercent(values.volatility21dPct)),
            metric('Faixa de preço', formatPercent(values.faixaPrecoPct)),
            metric('Max drawdown', formatPercent(values.maxDrawdownPct)),
            metric('Termômetro', typeof values.termometroLabel === 'string' ? values.termometroLabel : '—'),
          ]}
        </Box>
        {sectionLabel('Caixa')}
        <Typography variant='caption' color='text.secondary'>
          Cobertura de obrigações em {formatNumber(values.coberturaX)}x, com folga líquida em{' '}
          {values.folgaTrend === 'alta' || values.folgaTrend === 'queda' ? values.folgaTrend : '—'}.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            metric('Caixa', formatCompactCurrency(values.caixa)),
            metric('A receber', formatCompactCurrency(values.aReceber)),
            metric('A pagar', formatCompactCurrency(values.aPagar)),
            metric('Folga líquida', formatCompactCurrency(values.folgaLiquida)),
          ]}
        </Box>
        {sectionLabel('Carteira')}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 1,
          }}
        >
          {[
            metric('Tijolo', formatPercent(values.mixRealEstatePct)),
            metric('Papel', formatPercent(values.mixPapelPct)),
            metric('Top 5 papel', formatPercent(values.top5Pct)),
            metric('Inflação (IPCA+IGPM)', formatPercent(values.inflacaoPct)),
          ]}
        </Box>
        <Typography variant='caption' color='text.secondary'>
          {formatInteger(values.imoveisVacancia)} de {formatInteger(values.totalImoveis)} imóveis com vacância /{' '}
          {formatInteger(values.imoveisInadimplencia)} com inadimplência
        </Typography>
        <Stack direction='row' spacing={1} justifyContent='flex-end' alignItems='center'>
          <ButtonBase
            onClick={() => navigateTo('fii-resumo', { ticker })}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              typography: 'caption',
              fontWeight: 600,
              color: 'hsl(var(--primary))',
              '&:hover': {
                bgcolor: 'hsl(var(--muted))',
                textDecoration: 'underline',
              },
            }}
          >
            Ver resumo completo
            <ArrowUpRight size={14} aria-hidden='true' />
          </ButtonBase>
          {onAsk ? (
            <ButtonBase
              onClick={() => onAsk(`Fundos similares a ${ticker}`)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                typography: 'caption',
                fontWeight: 600,
                color: 'hsl(var(--primary))',
                '&:hover': {
                  bgcolor: 'hsl(var(--muted))',
                  textDecoration: 'underline',
                },
              }}
            >
              Fundos similares
            </ButtonBase>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='caption' color='text.secondary' display='block' noWrap>
        {label}
      </Typography>
      <Typography variant='body2' fontWeight={700} noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

const formatDiscovery = (value: unknown, format: ScreenerColumnFormat = 'number') => {
  if (typeof value !== 'number') return '—';
  if (format === 'currency')
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  if (format === 'compact-currency')
    return value >= 1e9
      ? `R$ ${(value / 1e9).toFixed(2)} bi`
      : value >= 1e6
        ? `R$ ${(value / 1e6).toFixed(2)} mi`
        : new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          }).format(value);
  if (format === 'percent') return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}%`;
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: format === 'integer' ? 0 : 2,
  }).format(value);
};
const DiscoveryLink = ({
  ticker,
  name,
  children,
}: {
  ticker: string;
  name?: string | null;
  children?: React.ReactNode;
}) => {
  const navigateTo = useNavigateTo();
  return (
    <ButtonBase
      onClick={() => navigateTo('fii-resumo', { ticker })}
      sx={{
        display: 'block',
        width: '100%',
        p: 0.75,
        textAlign: 'left',
        borderRadius: 1,
        '&:hover': { bgcolor: 'hsl(var(--muted))' },
      }}
    >
      <Stack direction='row' spacing={1} alignItems='center'>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='body2' fontWeight={700} sx={{ fontFamily: 'monospace' }}>
            {ticker}
          </Typography>
          <Typography variant='caption' color='text.secondary' noWrap>
            {name ?? '—'}
          </Typography>
        </Box>
        {children}
      </Stack>
    </ButtonBase>
  );
};
function ScreenerResultsRenderer({ data, onAsk }: { data: unknown; onAsk?: (prompt: string) => void }) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const value = (data ?? {}) as {
    filterSummary?: string[];
    count?: number;
    asOf?: string | null;
    fields?: Array<{
      key: string;
      label: string;
      format: ScreenerColumnFormat;
    }>;
    results?: Array<Record<string, unknown>>;
  };
  const fields = value.fields?.length
    ? value.fields
    : SCREENER_COLUMNS.filter((column) => ['dy12mPct', 'currentPvp', 'currentPrice'].includes(column.key));
  const selectedTickers = Array.from(selected);
  // The deterministic comparator flow extracts tickers from the message, so
  // the follow-up is only offered for B3 ticker codes it can parse.
  const comparable = selectedTickers.filter((ticker) => /^[A-Z]{4,6}11$/.test(ticker));
  const canCompare = comparable.length >= 2 && comparable.length === selectedTickers.length;
  const toggleSelected = (ticker: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });

  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Typography variant='caption' color='text.secondary'>
        Busca: {(value.filterSummary ?? ['Todos os fundos']).join(' · ')}
      </Typography>
      <Typography variant='body2' fontWeight={700}>
        {value.count ?? 0} fundos encontrados
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {(value.results ?? []).map((row) => (
          <Stack key={String(row.ticker)} direction='row' alignItems='center'>
            <Checkbox
              size='small'
              sx={{ p: 0.25, mr: 0.5 }}
              checked={selected.has(String(row.ticker))}
              onChange={() => toggleSelected(String(row.ticker))}
              inputProps={{ 'aria-label': `Selecionar ${String(row.ticker)} para comparar` }}
            />
            <DiscoveryLink ticker={String(row.ticker)} name={typeof row.name === 'string' ? row.name : null}>
              <Stack alignItems='flex-end'>
                {fields.map((field) => (
                  <Typography key={field.key} variant='caption' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {field.label}: {formatDiscovery(row[field.key], field.format)}
                  </Typography>
                ))}
              </Stack>
            </DiscoveryLink>
          </Stack>
        ))}
      </Stack>
      {onAsk && selectedTickers.length ? (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1 }}>
          <Button
            size='small'
            variant='outlined'
            disabled={!canCompare}
            onClick={() => onAsk(`Compare ${comparable.join(', ')}`)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Comparar selecionados
          </Button>
          {canCompare ? null : (
            <Typography variant='caption' color='text.secondary'>
              Selecione ao menos 2 tickers (ex.: HGLG11) para comparar.
            </Typography>
          )}
        </Stack>
      ) : null}
      <RendererFooter asOf={value.asOf} screenId='catalogo' linkLabel='Abrir catálogo' />
    </Paper>
  );
}
function RelatedFundsRenderer({ data }: { data: unknown }) {
  const related = (data as { related?: Array<Record<string, unknown>> })?.related ?? [];
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1 }}>
      <Stack spacing={0.5}>
        {related.map((row) => (
          <DiscoveryLink key={String(row.ticker)} ticker={String(row.ticker)} name={String(row.name ?? '')}>
            <Typography variant='caption'>Similaridade: {formatDiscovery(row.similarity)}</Typography>
          </DiscoveryLink>
        ))}
      </Stack>
    </Paper>
  );
}
function FundDividendsRenderer({ params, data }: { params: Record<string, unknown>; data: unknown }) {
  const value = (data ?? {}) as {
    name?: string;
    asOf?: string | null;
    dividends?: Array<Record<string, unknown>>;
  };
  const ticker = typeof params.ticker === 'string' ? params.ticker : null;
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1 }}>
      <Stack spacing={0.5}>
        {value.name ? (
          <Typography variant='body2' fontWeight={700}>
            {value.name}
          </Typography>
        ) : null}
        {(value.dividends ?? []).map((row, index) => (
          <Stack key={index} direction='row' justifyContent='space-between'>
            <Typography variant='caption'>
              {String(row.label ?? 'Provento')} · {String(row.eventDate ?? '—')}
            </Typography>
            <Typography variant='caption' sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatDiscovery(row.amount, 'currency')} · {formatDiscovery(row.dyEventPct, 'percent')}
            </Typography>
          </Stack>
        ))}
        <RendererFooter
          asOf={value.asOf}
          screenId={ticker ? 'fii-resumo' : undefined}
          params={ticker ? { ticker } : undefined}
          linkLabel='Ver fundo'
        />
      </Stack>
    </Paper>
  );
}

type MarketValue = {
  symbol?: string;
  label?: string;
  unit?: 'POINTS' | 'PERCENT' | 'BRL';
  value?: number | null;
  date?: string | null;
};

const formatMarketValue = (value: unknown, unit: MarketValue['unit']) => {
  if (typeof value !== 'number') return '—';
  if (unit === 'BRL')
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  const formatted = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 2,
  }).format(value);
  return unit === 'PERCENT' ? `${formatted}%` : formatted;
};

const formatMarketDate = (value: unknown) =>
  typeof value === 'string' ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${value}T00:00:00Z`)) : '—';

function MarketOverviewRenderer({ data }: { data: unknown }) {
  const navigateTo = useNavigateTo();
  const value = (data ?? {}) as {
    asOf?: string | null;
    selic?: MarketValue & {
      previousValue?: number | null;
      previousDate?: string | null;
    };
    indicators?: MarketValue[];
    currencies?: MarketValue[];
    movers?: {
      fii?: { top?: MarketMover[]; bottom?: MarketMover[] };
      fiagro?: { top?: MarketMover[]; bottom?: MarketMover[] };
    };
  };
  const indicators = value.indicators ?? [];
  const currencies = value.currencies ?? [];
  const selic = value.selic;
  const renderCard = (item: MarketValue) => (
    <Metric
      key={item.symbol}
      label={item.label ?? item.symbol ?? 'Indicador'}
      value={formatMarketValue(item.value, item.unit)}
    />
  );
  const renderMovers = (title: string, rows: MarketMover[], positive: boolean) => (
    <Box>
      <Typography variant='caption' color={positive ? 'success.main' : 'error.main'} fontWeight={700}>
        {title}
      </Typography>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        {rows.map((row) => (
          <ButtonBase
            key={row.ticker}
            onClick={() => navigateTo('fii-resumo', { ticker: row.ticker })}
            sx={{
              justifyContent: 'space-between',
              width: '100%',
              px: 0.5,
              borderRadius: 1,
            }}
          >
            <Typography variant='caption' fontWeight={700}>
              {row.ticker}
            </Typography>
            <Typography variant='caption' color={positive ? 'success.main' : 'error.main'}>
              {formatMarketValue(row.changePct, 'PERCENT')}
            </Typography>
          </ButtonBase>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.5 }}>
      <Stack spacing={1.25}>
        <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
          <Typography variant='body2' fontWeight={700}>
            Panorama do mercado
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {formatMarketDate(value.asOf)}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1,
          }}
        >
          <Box>
            <Metric label='Selic' value={formatMarketValue(selic?.value, 'PERCENT')} />
            <Stack direction='row' spacing={0.25} alignItems='center'>
              {typeof selic?.value === 'number' && typeof selic.previousValue === 'number' ? (
                selic.value >= selic.previousValue ? (
                  <ArrowUp size={13} color='green' />
                ) : (
                  <ArrowDown size={13} color='red' />
                )
              ) : null}
              <Typography variant='caption' color='text.secondary'>
                anterior: {formatMarketValue(selic?.previousValue, 'PERCENT')}
              </Typography>
            </Stack>
          </Box>
          {indicators.map(renderCard)}
        </Box>
        <Typography variant='caption' color='text.secondary' fontWeight={700}>
          Câmbio
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(3, 1fr)' },
            gap: 1,
          }}
        >
          {currencies.map(renderCard)}
        </Box>
        <Typography variant='caption' color='text.secondary' fontWeight={700}>
          Maiores altas/baixas
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)' },
            gap: 1,
          }}
        >
          {renderMovers('FII · altas', value.movers?.fii?.top ?? [], true)}
          {renderMovers('FII · baixas', value.movers?.fii?.bottom ?? [], false)}
          {renderMovers('FIAGRO · altas', value.movers?.fiagro?.top ?? [], true)}
          {renderMovers('FIAGRO · baixas', value.movers?.fiagro?.bottom ?? [], false)}
        </Box>
      </Stack>
    </Paper>
  );
}

type MarketMover = { ticker: string; changePct: number; latestClose: number };

function MarketSeriesRenderer({ params, data }: { params: Record<string, unknown>; data: unknown }) {
  const rows = (data as { rows?: MarketSeriesRow[] })?.rows ?? [];
  const fromMonth = typeof params.fromMonth === 'string' ? params.fromMonth : null;
  const toMonth = typeof params.toMonth === 'string' ? params.toMonth : null;
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.5}>
        {rows.map((row) => (
          <Typography key={row.symbol} variant='body2'>
            <b>{row.label}</b>: {formatMarketValue(row.start, row.unit)} → {formatMarketValue(row.end, row.unit)} (
            {formatMarketValue(row.changePct, 'PERCENT')})
          </Typography>
        ))}
        {fromMonth && toMonth ? (
          <RendererFooter period={`${fromMonth}–${toMonth}`} screenId='mercado' linkLabel='Abrir mercado' />
        ) : null}
      </Stack>
    </Paper>
  );
}

function WebSearchRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    answer?: string;
    sources?: Array<{ title?: string; url?: string }>;
  };
  const confirmation = useExternalLinkConfirm();
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
        {value.answer ?? ''}
      </Typography>
      {value.sources?.length ? (
        <Stack spacing={0.25} sx={{ mt: 1 }}>
          {value.sources.map((source) =>
            source.url ? (
              <ButtonBase
                key={source.url}
                onClick={() => confirmation.request(source.url!)}
                sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
              >
                <Typography variant='caption' color='primary'>
                  {source.title ?? source.url}
                </Typography>
              </ButtonBase>
            ) : null,
          )}
        </Stack>
      ) : null}
      {confirmation.pendingUrl ? (
        <ExternalLinkConfirmDialog
          url={confirmation.pendingUrl}
          open
          onClose={confirmation.close}
          onConfirm={confirmation.confirm}
        />
      ) : null}
    </Paper>
  );
}

function FundReportsRenderer({ params, data }: { params: Record<string, unknown>; data: unknown }) {
  const value = (data ?? {}) as {
    name?: string;
    asOf?: string | null;
    reports?: Array<Record<string, unknown>>;
  };
  const ticker = typeof params.ticker === 'string' ? params.ticker : null;
  const confirmation = useExternalLinkConfirm();
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        {value.name ? (
          <Typography variant='body2' fontWeight={700}>
            {value.name}
          </Typography>
        ) : null}
        {(value.reports ?? []).map((report) => (
          <Box key={String(report.id)}>
            <Typography variant='body2' fontWeight={700}>
              {String(report.documentDescription ?? report.documentType ?? 'Relatório')}
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block'>
              {[report.category, report.deliveryDate, report.sentimentoGeral].filter(Boolean).map(String).join(' · ') ||
                '—'}
            </Typography>
            {report.resumo ? (
              <Typography variant='caption' display='block'>
                {String(report.resumo)}
              </Typography>
            ) : null}
            {typeof report.pdfUrl === 'string' && report.pdfUrl ? (
              <ButtonBase
                onClick={() => confirmation.request(report.pdfUrl as string)}
                sx={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 1,
                  typography: 'caption',
                  fontWeight: 600,
                  color: 'hsl(var(--primary))',
                  '&:hover': {
                    bgcolor: 'hsl(var(--muted))',
                    textDecoration: 'underline',
                  },
                }}
              >
                Abrir PDF original
                <ArrowUpRight size={13} aria-hidden='true' />
              </ButtonBase>
            ) : null}
          </Box>
        ))}
        <RendererFooter
          asOf={value.asOf}
          screenId={ticker ? 'fii-resumo' : undefined}
          params={ticker ? { ticker } : undefined}
          linkLabel='Ver fundo'
        />
      </Stack>
      {confirmation.pendingUrl ? (
        <ExternalLinkConfirmDialog
          url={confirmation.pendingUrl}
          open
          onClose={confirmation.close}
          onConfirm={confirmation.confirm}
        />
      ) : null}
    </Paper>
  );
}

function ReportDetailsRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    ticker?: string;
    documentId?: number;
    documentDescription?: string | null;
    documentType?: string | null;
    category?: string | null;
    deliveryDate?: string | null;
    pdfUrl?: string | null;
    contentLength?: number;
    truncated?: boolean;
    excerpt?: string;
  };
  const confirmation = useExternalLinkConfirm();
  const formattedDate = value.deliveryDate
    ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${value.deliveryDate}T00:00:00Z`))
    : null;
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {value.documentDescription ?? value.documentType ?? 'Relatório'}
        </Typography>
        <Typography variant='caption' color='text.secondary' display='block'>
          {[value.category, formattedDate].filter(Boolean).join(' · ') || '—'}
          {typeof value.contentLength === 'number'
            ? ` · ${Math.round(value.contentLength / 1024)} KB de texto${value.truncated ? ' (truncado)' : ''}`
            : ''}
        </Typography>
        {value.excerpt ? (
          <Typography variant='caption' sx={{ whiteSpace: 'pre-wrap' }}>
            {value.excerpt}
          </Typography>
        ) : null}
        <Stack direction='row' spacing={1} alignItems='center'>
          {value.pdfUrl ? (
            <ButtonBase
              onClick={() => confirmation.request(value.pdfUrl as string)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                typography: 'caption',
                fontWeight: 600,
                color: 'hsl(var(--primary))',
                '&:hover': {
                  bgcolor: 'hsl(var(--muted))',
                  textDecoration: 'underline',
                },
              }}
            >
              Abrir PDF original
              <ArrowUpRight size={13} aria-hidden='true' />
            </ButtonBase>
          ) : null}
        </Stack>
      </Stack>
      {confirmation.pendingUrl ? (
        <ExternalLinkConfirmDialog
          url={confirmation.pendingUrl}
          open
          onClose={confirmation.close}
          onConfirm={confirmation.confirm}
        />
      ) : null}
    </Paper>
  );
}

function SavedSearchesRenderer({ data }: { data: unknown }) {
  const searches = Array.isArray((data as { searches?: unknown })?.searches)
    ? (data as { searches: Array<Record<string, unknown>> }).searches
    : [];
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.5}>
        {searches.length ? (
          searches.map((search) => (
            <Box key={String(search.id)}>
              <Typography variant='body2' fontWeight={700}>
                {String(search.name)}
              </Typography>
              {search.description ? (
                <Typography variant='caption' color='text.secondary'>
                  {String(search.description)}
                </Typography>
              ) : null}
            </Box>
          ))
        ) : (
          <Typography variant='caption' color='text.secondary'>
            Nenhuma busca salva.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

const formatPortfolioCurrency = (value: unknown) =>
  typeof value === 'number'
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 2,
      }).format(value)
    : '—';

const formatPortfolioDate = (value: unknown) =>
  typeof value === 'string' && value
    ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${value.slice(0, 10)}T00:00:00Z`))
    : '—';

function PortfolioListRenderer({ data }: { data: unknown }) {
  const portfolios = Array.isArray((data as { portfolios?: unknown })?.portfolios)
    ? (data as { portfolios: Array<Record<string, unknown>> }).portfolios
    : [];
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        {portfolios.length ? (
          portfolios.map((portfolio) => (
            <Box key={String(portfolio.id)}>
              <Typography variant='body2' fontWeight={700}>
                {String(portfolio.name)}
                {portfolio.isPrimary ? ' · principal' : ''}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {String(portfolio.processingStatus ?? 'sem processamento')}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant='caption' color='text.secondary'>
            Nenhuma carteira encontrada.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function PortfolioFullViewLink({
  portfolioId,
  tab,
}: {
  portfolioId?: string;
  tab: 'resumo' | 'dividendos' | 'patrimonio' | 'patrimonio/diversificacao';
}) {
  if (!portfolioId) return null;
  return (
    <ButtonBase
      component={Link}
      href={`/carteira/${encodeURIComponent(portfolioId)}/${tab}`}
      sx={{
        alignSelf: 'flex-start',
        mt: 0.5,
        px: 0.75,
        py: 0.4,
        borderRadius: 1,
        typography: 'caption',
        color: 'hsl(var(--primary))',
        '&:hover': {
          bgcolor: 'hsl(var(--muted))',
          textDecoration: 'underline',
        },
      }}
    >
      Abrir na carteira completa →
    </ButtonBase>
  );
}

function PortfolioOverviewRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    portfolio?: { id?: string; name?: string };
    asOf?: string | null;
    totals?: Record<string, unknown>;
    byFundType?: Array<{
      name?: string;
      fundType?: string;
      totalMarketValue?: number | null;
    }>;
    topPositions?: Array<Record<string, unknown>>;
  };
  const totals = value.totals ?? {};
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {value.portfolio?.name ?? 'Carteira'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Atualizado em {formatPortfolioDate(value.asOf)}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
          }}
        >
          <Metric label='Patrimônio' value={formatPortfolioCurrency(totals.totalMarketValue)} />
          <Metric label='Investido' value={formatPortfolioCurrency(totals.investedAmount)} />
          <Metric label='Resultado' value={formatPortfolioCurrency(totals.returnAmount)} />
          <Metric label='Renda 12m' value={formatPortfolioCurrency(totals.income12mAmount)} />
        </Box>
        {value.topPositions?.length ? (
          <Stack spacing={0.25}>
            <Typography variant='caption' color='text.secondary' fontWeight={700}>
              Maiores posições
            </Typography>
            {value.topPositions.slice(0, 5).map((position) => (
              <Stack key={String(position.assetCode)} direction='row' justifyContent='space-between' spacing={1}>
                <Typography variant='caption' noWrap>
                  <b>{String(position.assetCode)}</b>
                  {position.assetName ? ` · ${String(position.assetName)}` : ''}
                </Typography>
                <Typography variant='caption' fontWeight={700} noWrap>
                  {formatPortfolioCurrency(position.marketValue)}
                  {typeof position.portfolioPercent === 'number'
                    ? ` · ${position.portfolioPercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
                    : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        {value.byFundType?.length ? (
          <Stack spacing={0.25}>
            <Typography variant='caption' color='text.secondary' fontWeight={700}>
              Distribuição por tipo
            </Typography>
            {value.byFundType.map((item) => (
              <Stack key={item.fundType ?? item.name} direction='row' justifyContent='space-between' spacing={1}>
                <Typography variant='caption' noWrap>
                  {item.name ?? item.fundType ?? '—'}
                </Typography>
                <Typography variant='caption' fontWeight={700} noWrap>
                  {formatPortfolioCurrency(item.totalMarketValue)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        <PortfolioFullViewLink portfolioId={value.portfolio?.id} tab='resumo' />
      </Stack>
    </Paper>
  );
}

function PortfolioDividendsRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    portfolio?: { id?: string; name?: string };
    received12m?: number;
    monthlyAverage12m?: number;
    forecastAmount?: number;
    nextForecast?: {
      assetCode?: string;
      amount?: number;
      payDate?: string | null;
    } | null;
    receivedByMonth?: Array<{ month: string; label?: string; amount: number }>;
    forecastByMonth?: Array<{ month: string; label?: string; amount: number }>;
    topPayers?: Array<{
      assetCode: string;
      assetName?: string | null;
      amount: number;
      currentYieldPercent?: number | null;
    }>;
    asOf?: string | null;
  };
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {value.portfolio?.name ?? 'Dividendos'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          Dados até {formatPortfolioDate(value.asOf)}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
          }}
        >
          <Metric label='Recebido 12m' value={formatPortfolioCurrency(value.received12m)} />
          <Metric label='Média mensal' value={formatPortfolioCurrency(value.monthlyAverage12m)} />
          <Metric label='Previsto' value={formatPortfolioCurrency(value.forecastAmount)} />
          <Metric
            label='Próximo'
            value={value.nextForecast ? formatPortfolioCurrency(value.nextForecast.amount) : '—'}
          />
        </Box>
        {value.topPayers?.length ? (
          <Stack spacing={0.5}>
            <Typography variant='caption' color='text.secondary' fontWeight={700}>
              Maiores pagadores
            </Typography>
            {value.topPayers.slice(0, 5).map((item) => (
              <Stack key={item.assetCode} direction='row' justifyContent='space-between' spacing={1}>
                <Typography variant='caption' noWrap>
                  <b>{item.assetCode}</b>
                  {item.assetName ? ` · ${item.assetName}` : ''}
                </Typography>
                <Typography variant='caption' fontWeight={700} noWrap>
                  {formatPortfolioCurrency(item.amount)}
                  {typeof item.currentYieldPercent === 'number'
                    ? ` · DY ${item.currentYieldPercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
                    : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        {value.receivedByMonth?.length || value.forecastByMonth?.length ? (
          <Stack spacing={0.5}>
            <Typography variant='caption' color='text.secondary' fontWeight={700}>
              Evolução mensal
            </Typography>
            {[...(value.receivedByMonth ?? []), ...(value.forecastByMonth ?? [])]
              .slice(-12)
              .map((item, index, items) => {
                const max = Math.max(...items.map((entry) => entry.amount), 1);
                return (
                  <Stack key={`${item.month}-${index}`} direction='row' alignItems='center' spacing={0.75}>
                    <Typography variant='caption' sx={{ width: 58 }} noWrap>
                      {item.label ?? item.month}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: 7,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.max((item.amount / max) * 100, 2)}%`,
                          height: '100%',
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                    <Typography variant='caption' fontWeight={700} sx={{ width: 88, textAlign: 'right' }} noWrap>
                      {formatPortfolioCurrency(item.amount)}
                    </Typography>
                  </Stack>
                );
              })}
          </Stack>
        ) : null}
        <PortfolioFullViewLink portfolioId={value.portfolio?.id} tab='dividendos' />
      </Stack>
    </Paper>
  );
}

function PortfolioPatrimonyRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    portfolio?: { id?: string; name?: string };
    points?: Array<Record<string, unknown>>;
  };
  const points = value.points ?? [];
  const first = points[0];
  const last = points.at(-1);
  const numeric = (point: Record<string, unknown> | undefined, key: string) =>
    typeof point?.[key] === 'number' ? point[key] : 0;
  const chartPoints = points.slice(-12);
  const chartMax = Math.max(
    ...chartPoints.map((point) => Math.max(numeric(point, 'totalMarketValue'), numeric(point, 'investedAmount'))),
    1,
  );
  const latestResult = numeric(last, 'totalMarketValue') - numeric(last, 'investedAmount');
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {value.portfolio?.name ?? 'Patrimônio'}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {points.length} pontos mensais · {formatPortfolioDate(first?.month)} a {formatPortfolioDate(last?.month)}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
          }}
        >
          <Metric label='Atual' value={formatPortfolioCurrency(numeric(last, 'totalMarketValue'))} />
          <Metric label='Investido' value={formatPortfolioCurrency(numeric(last, 'investedAmount'))} />
          <Metric label='Resultado' value={formatPortfolioCurrency(latestResult)} />
          <Metric label='Renda acumulada' value={formatPortfolioCurrency(numeric(last, 'paidIncomeAmount'))} />
        </Box>
        {chartPoints.length ? (
          <Stack spacing={0.5} sx={{ mt: 0.25 }}>
            <Stack direction='row' justifyContent='space-between'>
              <Typography variant='caption' color='text.secondary' fontWeight={700}>
                Evolução mensal
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {formatPortfolioCurrency(latestResult)} de resultado atual
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 0.5,
                height: 116,
                px: 0.25,
                pt: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {chartPoints.map((point) => {
                const marketValue = numeric(point, 'totalMarketValue');
                const invested = numeric(point, 'investedAmount');
                return (
                  <Stack
                    key={String(point.month)}
                    spacing={0.25}
                    alignItems='center'
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Stack
                      direction='row'
                      alignItems='flex-end'
                      spacing={0.15}
                      sx={{
                        height: '100%',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        title={`Patrimônio ${formatPortfolioCurrency(marketValue)}`}
                        sx={{
                          width: '42%',
                          maxWidth: 14,
                          height: `${Math.max((marketValue / chartMax) * 100, 3)}%`,
                          bgcolor: 'primary.main',
                          borderRadius: '3px 3px 0 0',
                        }}
                      />
                      <Box
                        title={`Investido ${formatPortfolioCurrency(invested)}`}
                        sx={{
                          width: '42%',
                          maxWidth: 14,
                          height: `${Math.max((invested / chartMax) * 100, 3)}%`,
                          bgcolor: 'text.secondary',
                          opacity: 0.45,
                          borderRadius: '3px 3px 0 0',
                        }}
                      />
                    </Stack>
                    <Typography variant='caption' color='text.secondary' noWrap>
                      {formatPortfolioDate(point.month).slice(0, 5)}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
            <Stack direction='row' spacing={1.25}>
              <Typography variant='caption' color='primary.main'>
                ▮ Patrimônio
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                ▮ Investido
              </Typography>
            </Stack>
          </Stack>
        ) : null}
        {points.length ? (
          <Stack spacing={0.35}>
            <Typography variant='caption' color='text.secondary' fontWeight={700}>
              Histórico recente
            </Typography>
            {points
              .slice(-6)
              .reverse()
              .map((point) => {
                const result = numeric(point, 'totalMarketValue') - numeric(point, 'investedAmount');
                return (
                  <Stack key={String(point.month)} direction='row' justifyContent='space-between' spacing={1}>
                    <Typography variant='caption'>{formatPortfolioDate(point.month)}</Typography>
                    <Typography variant='caption' fontWeight={700} noWrap>
                      {formatPortfolioCurrency(numeric(point, 'totalMarketValue'))}
                      {` · ${formatPortfolioCurrency(result)}`}
                    </Typography>
                  </Stack>
                );
              })}
          </Stack>
        ) : null}
        <PortfolioFullViewLink portfolioId={value.portfolio?.id} tab='patrimonio' />
      </Stack>
    </Paper>
  );
}

function PortfolioDiversificationRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    portfolio?: { id?: string; name?: string };
    dimensions?: Array<{
      key: string;
      label: string;
      coveragePct: number;
      values: Array<{ label: string; portfolioPct: number }>;
    }>;
  };
  const chartColors = [
    'hsl(var(--primary))',
    'hsl(var(--secondary))',
    'hsl(38 72% 48%)',
    'hsl(198 58% 48%)',
    'hsl(280 45% 56%)',
    'hsl(12 68% 55%)',
  ];
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {value.portfolio?.name ?? 'Diversificação'}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          {(value.dimensions ?? []).slice(0, 6).map((dimension) => {
            let cursor = 0;
            const stops = dimension.values.slice(0, 6).map((item, index) => {
              const start = cursor;
              cursor += item.portfolioPct;
              return `${chartColors[index % chartColors.length]} ${start}% ${cursor}%`;
            });
            if (cursor < 100) {
              stops.push(`hsl(var(--muted)) ${cursor}% 100%`);
            }
            return (
              <Paper key={dimension.key} variant='outlined' sx={{ p: 1 }}>
                <Stack spacing={0.75}>
                  <Stack direction='row' justifyContent='space-between' spacing={1}>
                    <Typography variant='caption' fontWeight={700} noWrap>
                      {dimension.label}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' noWrap>
                      {dimension.coveragePct.toLocaleString('pt-BR', {
                        maximumFractionDigits: 1,
                      })}
                      % cobertura
                    </Typography>
                  </Stack>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Box
                      aria-label={`Distribuição por ${dimension.label}`}
                      role='img'
                      sx={{
                        flex: '0 0 auto',
                        width: 82,
                        height: 82,
                        borderRadius: '50%',
                        background: stops.length ? `conic-gradient(${stops.join(', ')})` : 'hsl(var(--muted))',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 18,
                          borderRadius: '50%',
                          bgcolor: 'hsl(var(--card))',
                        },
                      }}
                    />
                    <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                      {dimension.values.slice(0, 4).map((item, index) => (
                        <Stack key={item.label} direction='row' spacing={0.5} alignItems='center'>
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              flex: '0 0 auto',
                              bgcolor: chartColors[index % chartColors.length],
                            }}
                          />
                          <Typography variant='caption' noWrap>
                            {item.label} ·{' '}
                            {item.portfolioPct.toLocaleString('pt-BR', {
                              maximumFractionDigits: 1,
                            })}
                            %
                          </Typography>
                        </Stack>
                      ))}
                      {!dimension.values.length ? (
                        <Typography variant='caption' color='text.secondary'>
                          Sem valores mapeados
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
        <PortfolioFullViewLink portfolioId={value.portfolio?.id} tab='patrimonio/diversificacao' />
      </Stack>
    </Paper>
  );
}

function PortfolioProcessingStatusRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as {
    portfolio?: {
      name?: string;
      processingStatus?: string | null;
      processingMessage?: string | null;
      processingError?: string | null;
    };
    latestImport?: {
      status?: string;
      fileName?: string | null;
      errorMessage?: string | null;
    } | null;
    diagnostic?: { status?: string; asOf?: string } | null;
  };
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.5}>
        <Typography variant='body2' fontWeight={700}>
          {value.portfolio?.name ?? 'Processamento'}
        </Typography>
        <Typography variant='caption'>Carteira: {value.portfolio?.processingStatus ?? 'sem status'}</Typography>
        {value.portfolio?.processingMessage ? (
          <Typography variant='caption' color='text.secondary'>
            {value.portfolio.processingMessage}
          </Typography>
        ) : null}
        {value.portfolio?.processingError ? (
          <Typography variant='caption' color='error'>
            {value.portfolio.processingError}
          </Typography>
        ) : null}
        <Typography variant='caption' color='text.secondary'>
          Última importação: {value.latestImport?.status ?? 'nenhuma'}
          {value.latestImport?.fileName ? ` · ${value.latestImport.fileName}` : ''}
        </Typography>
        {value.diagnostic ? (
          <Typography variant='caption' color='text.secondary'>
            Diagnóstico: {value.diagnostic.status} · {formatPortfolioDate(value.diagnostic.asOf)}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function FixedIncomeRenderer({ data }: { data: unknown }) {
  const value = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(value.results) ? value.results : [value];
  const text = (row: Record<string, unknown>, key: string) =>
    typeof row[key] === 'string' || typeof row[key] === 'number' ? String(row[key]) : '—';
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {rows.length > 1 ? `${rows.length} títulos de renda fixa` : text(rows[0] as Record<string, unknown>, 'name')}
        </Typography>
        {rows.slice(0, 10).map((item, index) => {
          const row = (item ?? {}) as Record<string, unknown>;
          const url = typeof row.screen_url === 'string' ? row.screen_url : null;
          return (
            <Box
              key={`${text(row, 'idCem')}-${index}`}
              sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}
            >
              <Typography variant='body2' fontWeight={600}>
                {text(row, 'name')} · {text(row, 'idCem')}
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block'>
                Indexador: {text(row, 'indexer')} · Taxa: {text(row, 'sellRateMorning')} · PU:{' '}
                {text(row, 'sellPuMorning')}
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block'>
                Vencimento: {text(row, 'maturityDate')} · Risco: {text(row, 'riskFactor')}
              </Typography>
              {Array.isArray(row.cashFlows) ? (
                <Typography variant='caption' color='text.secondary' display='block'>
                  Fluxos de caixa: {row.cashFlows.length}
                </Typography>
              ) : null}
              {Array.isArray(row.history) ? (
                <Typography variant='caption' color='text.secondary' display='block'>
                  Cotações históricas: {row.history.length}
                </Typography>
              ) : null}
              {url ? (
                <Link href={url} target='_blank' rel='noopener noreferrer'>
                  Abrir tela completa
                </Link>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function PortfolioActivityRenderer({ data, kind }: { data: unknown; kind: 'positions' | 'movements' }) {
  const value = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(value[kind]) ? value[kind] : [];
  const scalar = (candidate: unknown) =>
    typeof candidate === 'string' || typeof candidate === 'number' || typeof candidate === 'boolean'
      ? String(candidate)
      : '—';
  return (
    <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
      <Stack spacing={0.75}>
        <Typography variant='body2' fontWeight={700}>
          {kind === 'positions' ? 'Posições atuais' : 'Movimentações recentes'}
        </Typography>
        {rows.slice(0, 10).map((item, index) => {
          const row = (item ?? {}) as Record<string, unknown>;
          const label = `${String(row.assetCode ?? 'Ativo')} · ${String(row.assetName ?? '')}`.trim();
          const date = kind === 'positions' ? row.asOf : row.occurredAt;
          const detail =
            kind === 'positions'
              ? `Qtd.: ${scalar(row.quantity)} · Valor: ${scalar(row.marketValue)} · Peso: ${scalar(row.portfolioPercent)}%`
              : `${scalar(row.transactionType) === '—' ? 'Movimentação' : scalar(row.transactionType)} · Qtd.: ${scalar(row.quantity)} · Valor: ${scalar(row.netAmount ?? row.grossAmount)}`;
          return (
            <Box
              key={`${String(row.id ?? row.assetCode ?? 'row')}-${index}`}
              sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 0.75 }}
            >
              <Typography variant='body2' fontWeight={600}>
                {label}
              </Typography>
              <Typography variant='caption' color='text.secondary' display='block'>
                {scalar(date)} · {detail}
              </Typography>
            </Box>
          );
        })}
        {rows.length > 10 ? (
          <Typography variant='caption' color='text.secondary'>
            Exibindo os 10 primeiros registros.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

type CourseLessonView = {
  trackTitle?: unknown;
  courseTitle?: unknown;
  moduleTitle?: unknown;
  topicTitle?: unknown;
  moduleDurationMinutes?: unknown;
  snippet?: unknown;
  url?: unknown;
  progress?: unknown;
  status?: unknown;
  label?: unknown;
};

type CourseTrackProgressView = {
  title?: unknown;
  progress?: unknown;
  status?: unknown;
};

const courseText = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const courseStatusText = (status: unknown) =>
  status === 'completed' ? 'Concluída' : status === 'in-progress' ? 'Em andamento' : 'Não iniciada';

function CourseLessonButton({ url, label }: { url: string; label: string }) {
  const router = useRouter();
  return (
    <Button
      variant='text'
      size='small'
      endIcon={<ArrowUpRight size={14} />}
      onClick={() => router.push(url)}
      sx={{ flexShrink: 0, alignSelf: 'center' }}
    >
      {label}
    </Button>
  );
}

function CourseLessonCard({ lesson, defaultLabel }: { lesson: CourseLessonView; defaultLabel: string }) {
  const topicTitle = courseText(lesson.topicTitle);
  const url = courseText(lesson.url);
  if (!topicTitle || !url) return null;
  const context = [courseText(lesson.trackTitle), courseText(lesson.courseTitle), courseText(lesson.moduleTitle)]
    .filter(Boolean)
    .join(' · ');
  const duration = typeof lesson.moduleDurationMinutes === 'number' ? lesson.moduleDurationMinutes : null;
  const progress = typeof lesson.progress === 'number' ? lesson.progress : null;
  const snippet = courseText(lesson.snippet);
  return (
    <Paper variant='outlined' sx={{ p: 1.25 }}>
      <Stack direction='row' spacing={1} alignItems='center'>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='body2' fontWeight={700}>
            {topicTitle}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            {[
              context,
              duration !== null ? `${duration} min` : null,
              progress !== null ? `${progress}% concluído` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {snippet ? (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
              {snippet}
            </Typography>
          ) : null}
        </Box>
        <CourseLessonButton url={url} label={courseText(lesson.label) ?? defaultLabel} />
      </Stack>
    </Paper>
  );
}

function CourseLessonRenderer({ data }: { data: unknown }) {
  const view = (typeof data === 'object' && data !== null ? data : {}) as {
    resume?: unknown;
    tracks?: unknown;
    failedExercises?: unknown;
  };
  const resume = typeof view.resume === 'object' && view.resume !== null ? (view.resume as CourseLessonView) : null;
  const tracks = Array.isArray(view.tracks) ? (view.tracks as CourseTrackProgressView[]) : [];
  const failed = Array.isArray(view.failedExercises)
    ? (view.failedExercises as Array<{
        courseTitle?: unknown;
        topicTitle?: unknown;
        url?: unknown;
        score?: unknown;
      }>)
    : [];
  if (!resume && !tracks.length && !failed.length) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {resume ? <CourseLessonCard lesson={resume} defaultLabel='Abrir aula' /> : null}
      {failed.map((exercise, index) => {
        const topicTitle = courseText(exercise.topicTitle);
        const url = courseText(exercise.url);
        if (!topicTitle || !url) return null;
        const score = typeof exercise.score === 'number' ? Math.round(exercise.score) : null;
        return (
          <Paper variant='outlined' key={`${topicTitle}-${index}`} sx={{ p: 1.25 }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant='body2' fontWeight={700}>
                  {topicTitle}
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  {[courseText(exercise.courseTitle), score !== null ? `Nota ${score}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
              <CourseLessonButton url={url} label='Revisar' />
            </Stack>
          </Paper>
        );
      })}
      {tracks.length ? (
        <Typography variant='caption' color='text.secondary'>
          {tracks
            .map((track) => {
              const title = courseText(track.title);
              const progress = typeof track.progress === 'number' ? track.progress : null;
              return title ? `${title}: ${progress ?? 0}% · ${courseStatusText(track.status)}` : null;
            })
            .filter(Boolean)
            .join(' | ')}
        </Typography>
      ) : null}
    </Stack>
  );
}

function CourseSearchResultsRenderer({ data }: { data: unknown }) {
  const view = (typeof data === 'object' && data !== null ? data : {}) as { results?: unknown };
  const results = Array.isArray(view.results) ? (view.results as CourseLessonView[]) : [];
  const visible = results.slice(0, 5);
  if (!visible.length) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {visible.map((lesson, index) => (
        <CourseLessonCard key={index} lesson={lesson} defaultLabel='Abrir aula' />
      ))}
      {results.length > visible.length ? (
        <Typography variant='caption' color='text.secondary'>
          Exibindo as {visible.length} primeiras aulas.
        </Typography>
      ) : null}
    </Stack>
  );
}

type CourseOutlineTopicView = {
  title?: unknown;
  type?: unknown;
  access?: unknown;
  url?: unknown;
  synopsis?: unknown;
};

type CourseOutlineDataView = {
  scope?: unknown;
  course?: unknown;
  tracks?: unknown;
  untrackedCourses?: unknown;
};

const outlineText = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);

function CourseOutlineTopicRow({ topic }: { topic: CourseOutlineTopicView }) {
  const router = useRouter();
  const title = outlineText(topic.title);
  const url = outlineText(topic.url);
  if (!title || !url) return null;
  const access = topic.access === 'pro' ? 'Pro' : topic.access === 'free' ? 'Grátis' : null;
  const type = topic.type === 'exercise_set' ? 'Exercícios' : 'Teoria';
  return (
    <ButtonBase
      component='div'
      onClick={() => router.push(url)}
      sx={{
        display: 'block',
        width: '100%',
        px: 0.75,
        py: 0.5,
        borderRadius: 1,
        textAlign: 'left',
        '&:hover': { bgcolor: 'hsl(var(--muted))' },
      }}
    >
      <Typography variant='caption' sx={{ display: 'block' }}>
        {title}
        <Box component='span' color='text.secondary'>
          {' · '}
          {type}
          {access ? ` · ${access}` : ''}
        </Box>
      </Typography>
      {outlineText(topic.synopsis) ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
          {outlineText(topic.synopsis)}
        </Typography>
      ) : null}
    </ButtonBase>
  );
}

function CourseOutlineCourseView({ course, withTopics }: { course: unknown; withTopics: boolean }) {
  const view = (typeof course === 'object' && course !== null ? course : {}) as Record<string, unknown>;
  const title = outlineText(view.title);
  if (!title) return null;
  const meta = [
    outlineText(view.level),
    typeof view.totalMinutes === 'number' ? `${view.totalMinutes} min` : null,
    typeof view.moduleCount === 'number' ? `${view.moduleCount} módulos` : null,
    typeof view.topicCount === 'number' ? `${view.topicCount} aulas` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const modules = Array.isArray(view.modules) ? (view.modules as Record<string, unknown>[]) : [];
  return (
    <Box>
      <Typography variant='body2' fontWeight={700}>
        {title}
      </Typography>
      {meta ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
          {meta}
        </Typography>
      ) : null}
      {outlineText(view.description) ? (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
          {outlineText(view.description)}
        </Typography>
      ) : null}
      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
        {modules.map((module, index) => {
          const moduleTitle = outlineText(module.title);
          if (!moduleTitle) return null;
          const duration = typeof module.durationMinutes === 'number' ? module.durationMinutes : null;
          return (
            <Box key={`${moduleTitle}-${index}`} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 700 }}>
                {moduleTitle}
                {duration !== null ? ` · ${duration} min` : ''}
              </Typography>
              {outlineText(module.description) ? (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  {outlineText(module.description)}
                </Typography>
              ) : null}
              {withTopics && Array.isArray(module.topics) ? (
                <Stack sx={{ mt: 0.5 }}>
                  {(module.topics as CourseOutlineTopicView[]).map((topic, topicIndex) => (
                    <CourseOutlineTopicRow key={`${moduleTitle}-${topicIndex}`} topic={topic} />
                  ))}
                </Stack>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function CourseOutlineRenderer({ data }: { data: unknown }) {
  const view = (typeof data === 'object' && data !== null ? data : {}) as CourseOutlineDataView;
  if (view.scope === 'course' && typeof view.course === 'object' && view.course !== null) {
    return (
      <Paper variant='outlined' sx={{ mt: 1, p: 1.25 }}>
        <CourseOutlineCourseView course={view.course} withTopics />
      </Paper>
    );
  }
  const tracks = Array.isArray(view.tracks) ? (view.tracks as unknown[]) : [];
  const untracked = Array.isArray(view.untrackedCourses) ? (view.untrackedCourses as unknown[]) : [];
  if (!tracks.length && !untracked.length) return null;
  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {tracks.map((track, index) => {
        const trackView = (typeof track === 'object' && track !== null ? track : {}) as Record<string, unknown>;
        const trackTitle = outlineText(trackView.title);
        if (!trackTitle) return null;
        const courses = Array.isArray(trackView.courses) ? (trackView.courses as unknown[]) : [];
        return (
          <Paper variant='outlined' key={`${trackTitle}-${index}`} sx={{ p: 1.25 }}>
            <Typography variant='body2' fontWeight={700}>
              {trackTitle}
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.75 }}>
              {courses.map((course, courseIndex) => (
                <CourseOutlineCourseView key={`${trackTitle}-${courseIndex}`} course={course} withTopics={false} />
              ))}
            </Stack>
          </Paper>
        );
      })}
      {untracked.map((course, index) => (
        <Paper variant='outlined' key={`untracked-${index}`} sx={{ p: 1.25 }}>
          <CourseOutlineCourseView course={course} withTopics={false} />
        </Paper>
      ))}
    </Stack>
  );
}

type MarketSeriesRow = {
  symbol: string;
  label: string;
  unit: MarketValue['unit'];
  start: number | null;
  end: number | null;
  changePct: number | null;
};

export function ToolRenderer({
  descriptor,
  onAsk,
}: {
  descriptor: RendererDescriptor;
  onAsk?: (prompt: string) => void;
}) {
  switch (descriptor.kind) {
    case 'screen-list':
      return <ScreenListRenderer data={descriptor.data} />;
    case 'screen-link':
      return <ScreenLinkRenderer params={descriptor.params} data={descriptor.data} />;
    case 'external-link':
      return <ExternalLinkRenderer params={descriptor.params} />;
    case 'ticker-summary':
      return <InstrumentSummaryRenderer params={descriptor.params} data={descriptor.data} onAsk={onAsk} />;
    case 'screener-results':
      return <ScreenerResultsRenderer data={descriptor.data} onAsk={onAsk} />;
    case 'comparator':
      return (
        <ComparadorClient
          embedded
          initialAssets={
            Array.isArray(descriptor.params.tickers)
              ? descriptor.params.tickers.filter((ticker): ticker is string => typeof ticker === 'string')
              : []
          }
          initialIndicators={
            Array.isArray(descriptor.params.indicators)
              ? descriptor.params.indicators.filter((indicator): indicator is string => typeof indicator === 'string')
              : []
          }
        />
      );
    case 'related-funds':
      return <RelatedFundsRenderer data={descriptor.data} />;
    case 'fund-dividends':
      return <FundDividendsRenderer params={descriptor.params} data={descriptor.data} />;
    case 'market-overview':
      return <MarketOverviewRenderer data={descriptor.data} />;
    case 'market-series':
      return <MarketSeriesRenderer params={descriptor.params} data={descriptor.data} />;
    case 'web-search':
      return <WebSearchRenderer data={descriptor.data} />;
    case 'fund-reports':
      return <FundReportsRenderer params={descriptor.params} data={descriptor.data} />;
    case 'report-details':
      return <ReportDetailsRenderer data={descriptor.data} />;
    case 'saved-searches':
      return <SavedSearchesRenderer data={descriptor.data} />;
    case 'portfolio-list':
      return <PortfolioListRenderer data={descriptor.data} />;
    case 'portfolio-overview':
      return <PortfolioOverviewRenderer data={descriptor.data} />;
    case 'portfolio-dividends':
      return <PortfolioDividendsRenderer data={descriptor.data} />;
    case 'portfolio-patrimony':
      return <PortfolioPatrimonyRenderer data={descriptor.data} />;
    case 'portfolio-diversification':
      return <PortfolioDiversificationRenderer data={descriptor.data} />;
    case 'portfolio-processing-status':
      return <PortfolioProcessingStatusRenderer data={descriptor.data} />;
    case 'fixed-income-details':
    case 'fixed-income-search-results':
    case 'fixed-income-comparison':
    case 'fixed-income-history':
      return <FixedIncomeRenderer data={descriptor.data} />;
    case 'portfolio-positions':
      return <PortfolioActivityRenderer data={descriptor.data} kind='positions' />;
    case 'portfolio-movements':
      return <PortfolioActivityRenderer data={descriptor.data} kind='movements' />;
    case 'course-lesson':
      return <CourseLessonRenderer data={descriptor.data} />;
    case 'course-search-results':
      return <CourseSearchResultsRenderer data={descriptor.data} />;
    case 'course-outline':
      return <CourseOutlineRenderer data={descriptor.data} />;
    default:
      return null;
  }
}

export function CollapsibleToolRenderer({
  descriptor,
  expanded,
  onToggle,
  onAsk,
}: {
  descriptor: RendererDescriptor;
  expanded: boolean;
  onToggle: () => void;
  onAsk?: (prompt: string) => void;
}) {
  const summary = summarizeRenderer(descriptor);

  return (
    <Box sx={{ mt: 1 }}>
      <ButtonBase
        aria-expanded={expanded}
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          px: 1,
          py: 0.75,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'hsl(var(--border))',
          bgcolor: 'hsl(var(--card))',
          textAlign: 'left',
          width: '100%',
          '&:hover': { bgcolor: 'hsl(var(--muted))' },
        }}
      >
        <ChevronRight
          size={14}
          style={{
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            marginTop: 1,
          }}
        />
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{
            flex: 1,
            ml: 0.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
          }}
        >
          {summary}
        </Typography>
      </ButtonBase>
      {expanded ? <ToolRenderer descriptor={descriptor} onAsk={onAsk} /> : null}
    </Box>
  );
}
